import { useEffect, useRef, useState, useCallback } from "react";
import { logPlay } from "../api";

const GCS = "https://storage.googleapis.com/myaimusic";
const GENRE_COLS = {
  pop:        "#7adb2a",
  electronic: "#9b27af",
  rock:       "#ff7a00",
};

export default function Player({
  song, songs, queue, onSelect, onNext,
  onAddToQueue, onRemoveFromQueue, queueOpen, onToggleQueue
}) {
  const audioRef    = useRef(null);
  const scrubberRef = useRef(null);
  const vizRef      = useRef(null);
  const scrubbing   = useRef(false);

  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [shuffle, setShuffle]   = useState(false);
  const [repeat, setRepeat]     = useState(false);
  const [muted, setMuted]       = useState(false);
  const [volume, setVolume]     = useState(80);

  const col      = GENRE_COLS[song.genre?.toLowerCase()] || "#ff2d9b";
  const coverUrl = song.cover_file ? `${GCS}/cover_art/${song.cover_file}` : null;
  const pct      = duration ? (progress / duration) * 100 : 0;

  // ── Load song ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!song) return;
    const audio = audioRef.current;
    audio.src = `${GCS}/mp3/${song.mp3_file}`;
    audio.play().catch(console.warn);
    setPlaying(true);
    setProgress(0);
    logPlay(song.id, song.title).catch(console.warn);
  }, [song]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Volume ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // ── Audio events + milestones ──────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => { if (!scrubbing.current) setProgress(audio.currentTime); };
    const onLoad = () => setDuration(audio.duration);
    const onEnd  = () => {
      if (repeat) { audio.currentTime = 0; audio.play(); return; }
      onNext(song);
    };

    const milestones = { 25: false, 50: false, 75: false, 100: false };
    const onMilestone = () => {
      if (!audio.duration) return;
      const pct  = (audio.currentTime / audio.duration) * 100;
      const secs = Math.floor(audio.currentTime);
      if (pct >= 25 && !milestones[25])  { milestones[25]  = true; logPlay(song.id, song.title, "25%",      secs); }
      if (pct >= 50 && !milestones[50])  { milestones[50]  = true; logPlay(song.id, song.title, "50%",      secs); }
      if (pct >= 75 && !milestones[75])  { milestones[75]  = true; logPlay(song.id, song.title, "75%",      secs); }
      if (pct >= 99 && !milestones[100]) { milestones[100] = true; logPlay(song.id, song.title, "complete", secs); }
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("timeupdate", onMilestone);
    audio.addEventListener("loadedmetadata", onLoad);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("timeupdate", onMilestone);
      audio.removeEventListener("loadedmetadata", onLoad);
      audio.removeEventListener("ended", onEnd);
    };
  }, [song, repeat, onNext]);

  // ── Visualizer ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vizRef.current) return;
    const bars = vizRef.current.querySelectorAll(".pl-viz-bar");
    const interval = setInterval(() => {
      if (!playing) return;
      bars.forEach(b => {
        b.style.height = Math.floor(3 + Math.random() * 17) + "px";
      });
    }, 120);
    return () => clearInterval(interval);
  }, [playing, col]);

  // ── togglePlay — defined BEFORE keyboard useEffect ─────────────────────────
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().catch(console.warn); setPlaying(true); }
  }, [playing]);

  // ── showHint ───────────────────────────────────────────────────────────────
  const showHint = useCallback((msg) => {
    const el = document.getElementById("kb-hint");
    if (!el) return;
    el.textContent = msg;
    el.classList.add("show");
    setTimeout(() => el.classList.remove("show"), 1200);
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
        showHint("Space: Play / Pause");
      } else if (e.code === "ArrowLeft" && !e.shiftKey) {
        e.preventDefault();
        const audio = audioRef.current;
        audio.currentTime = Math.max(0, audio.currentTime - 10);
        showHint("← 10s back");
      } else if (e.code === "ArrowRight" && !e.shiftKey) {
        e.preventDefault();
        const audio = audioRef.current;
        audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
        showHint("→ 10s forward");
      } else if (e.code === "ArrowLeft" && e.shiftKey) {
        e.preventDefault();
        const i = songs.findIndex(s => s.id === song.id);
        onSelect(songs[(i - 1 + songs.length) % songs.length]);
        showHint("Shift+← Prev track");
      } else if (e.code === "ArrowRight" && e.shiftKey) {
        e.preventDefault();
        onNext(song);
        showHint("Shift+→ Next track");
      } else if (e.key === "r" || e.key === "R") {
        setRepeat(r => !r);
        showHint("R: Repeat");
      } else if (e.key === "s" || e.key === "S") {
        setShuffle(s => !s);
        showHint("S: Shuffle");
      } else if (e.key === "q" || e.key === "Q") {
        onToggleQueue();
        showHint("Q: Queue");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [song, songs, onSelect, onNext, onToggleQueue, showHint, togglePlay]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function handleVolumeChange(v) {
    setVolume(v);
    audioRef.current.volume = v / 100;
    setMuted(v === 0);
    audioRef.current.muted = v === 0;
  }

  function toggleMute() {
    const newMuted = !muted;
    setMuted(newMuted);
    audioRef.current.muted = newMuted;
  }

  function onScrubStart(e) {
    scrubbing.current = true;
    doScrub(e);
    const onMove = ev => doScrub(ev);
    const onUp   = ev => {
      const p = getScrubPct(ev);
      audioRef.current.currentTime = p * duration;
      scrubbing.current = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function getScrubPct(e) {
    const rect = scrubberRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }

  function doScrub(e) {
    const p = getScrubPct(e);
    setProgress(p * duration);
  }

  function prev() {
    const i = songs.findIndex(s => s.id === song.id);
    onSelect(songs[(i - 1 + songs.length) % songs.length]);
  }

  function fmt(s) {
    if (!s || isNaN(s)) return "0:00";
    return Math.floor(s / 60) + ":" + String(Math.floor(s % 60)).padStart(2, "0");
  }

  const showToast = useCallback((msg) => {
    const el = document.getElementById("toast");
    if (!el) return;
    el.textContent = msg;
    el.classList.add("show");
    setTimeout(() => el.classList.remove("show"), 1500);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <audio ref={audioRef} />

      {/* Queue panel */}
      <div className={`queue-panel ${queueOpen ? "open" : ""}`}>
        <div className="queue-header">
          <span className="queue-title">Up Next</span>
          <button className="queue-close" onClick={onToggleQueue}>×</button>
        </div>
        <div className="queue-list">
          {queue.length === 0 ? (
            <div className="queue-empty">Queue is empty — hover a track and click + Queue</div>
          ) : (
            queue.map((t, i) => (
              <div
                key={t.id}
                className={`queue-item ${song?.id === t.id ? "playing" : ""}`}
                onClick={() => onSelect(t)}
              >
                <span className="qi-num">{i + 1}</span>
                <span className="qi-name">{t.title}</span>
                <span className="qi-dur">{t.duration}</span>
                <button
                  className="qi-remove"
                  onClick={e => { e.stopPropagation(); onRemoveFromQueue(t.id); }}
                >×</button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Player bar */}
      <div className="player">
        <div className="pl-inner">
          <div className="pl-top">

            <div className="pl-thumb">
              {coverUrl
                ? <img src={coverUrl} alt={song.title} />
                : <div style={{ width: "100%", height: "100%", background: col + "33" }} />
              }
            </div>

            <div className="pl-viz" ref={vizRef}>
              {[0,1,2,3,4,5,6,7].map((_, i) => (
                <div key={i} className="pl-viz-bar" style={{ background: col, height: "4px" }} />
              ))}
            </div>

            <div className="pl-left">
              <div className="pl-title">{song.title}</div>
              <div className="pl-meta">{song.model} · {song.genre} · {song.key}</div>
            </div>

            <div className="pl-ctrls">
              <button className={`pctrl ${shuffle ? "active" : ""}`}
                onClick={() => { setShuffle(s => !s); showToast(shuffle ? "Shuffle off" : "Shuffle on"); }}
                title="Shuffle (S)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 16l-4 4v-3H3v-2h14v-3l4 4zM3 8h14V5l4 4-4 4V10H3V8z"/>
                </svg>
              </button>
              <button className="pctrl" onClick={prev} title="Prev (Shift+←)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                </svg>
              </button>
              <button className="pbig" style={{ background: col }} onClick={togglePlay} title="Play/Pause (Space)">
                {playing
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                }
              </button>
              <button className="pctrl" onClick={() => onNext(song)} title="Next (Shift+→)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z"/>
                </svg>
              </button>
              <button className={`pctrl ${repeat ? "active" : ""}`}
                onClick={() => { setRepeat(r => !r); showToast(repeat ? "Repeat off" : "Repeat on"); }}
                title="Repeat (R)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
                </svg>
              </button>
              <button className={`pctrl ${queueOpen ? "active" : ""}`}
                onClick={onToggleQueue} title="Queue (Q)" style={{ marginLeft: 4 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
                </svg>
              </button>
            </div>

            <div className="vol-wrap">
              <button className="vol-btn" onClick={toggleMute}>
                {muted
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
                }
              </button>
              <input
                className="vol-slider"
                type="range" min="0" max="100"
                value={muted ? 0 : volume}
                onChange={e => handleVolumeChange(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="scrubber-row">
            <span className="wtime">{fmt(progress)}</span>
            <div className="scrubber" ref={scrubberRef}
              onMouseDown={onScrubStart} onTouchStart={onScrubStart}>
              <div className="scrubber-track" />
              <div className="scrubber-fill" style={{ width: `${pct}%`, background: col }} />
              <div className="scrubber-thumb" style={{ left: `${pct}%`, borderColor: col }} />
            </div>
            <span className="wtime r">{fmt(duration)}</span>
          </div>
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="kb-hint" id="kb-hint" />

      {/* Toast */}
      <div className="toast" id="toast" />
    </>
  );
}